import { useTranslation } from 'react-i18next'
import {
  getGuideStoryLayout,
  guideParagraphKeys,
  type GuideStoryId,
  type GuideStoryLayout,
  type GuideStorySection,
  type GuideStoryTip,
} from '@redevicer/shared'
import ModalCloseButton from './ModalCloseButton'
import { getStoryCoverUrl } from '../utils/homeStoryImages'

type Props = {
  storyId: GuideStoryId
  onClose: () => void
}

function GuideTipList({ tips, t }: { tips: GuideStoryTip[]; t: (key: string) => string }) {
  return (
    <ul className="vc-guide-tips">
      {tips.map((tip) => (
        <li key={tip.labelKey} className="vc-guide-tip">
          <span className="vc-guide-tip-label">{t(tip.labelKey)}</span>
          <p className="vc-guide-tip-body">{t(tip.bodyKey)}</p>
        </li>
      ))}
    </ul>
  )
}

function GuideStepList({ keys, t }: { keys: string[]; t: (key: string) => string }) {
  return (
    <ol className="vc-guide-steps">
      {keys.map((key) => (
        <li key={key} className="vc-guide-step">
          <p>{t(key)}</p>
        </li>
      ))}
    </ol>
  )
}

function GuideStorySections({
  layout,
  t,
}: {
  layout: GuideStoryLayout
  t: (key: string) => string
}) {
  return (
    <div className="vc-guide-modal-body vc-guide-modal-body--structured">
      <p className="vc-guide-modal-lead">{t(layout.leadKey)}</p>
      {layout.sections.map((section, index) => (
        <GuideSectionBlock key={section.titleKey ?? section.bodyKey ?? `section-${index}`} section={section} t={t} />
      ))}
    </div>
  )
}

function GuideSectionBlock({
  section,
  t,
}: {
  section: GuideStorySection
  t: (key: string) => string
}) {
  const hasTitle = Boolean(section.titleKey)
  const hasBodyOnly = Boolean(section.bodyKey) && !section.tips?.length && !section.bulletKeys?.length && !section.subsections?.length

  if (hasBodyOnly && !hasTitle) {
    return <p className="vc-guide-footnote">{t(section.bodyKey!)}</p>
  }

  return (
    <section className="vc-guide-section">
      {hasTitle ? <h3 className="vc-guide-section-title">{t(section.titleKey!)}</h3> : null}
      {section.bodyKey && !hasBodyOnly ? <p className="vc-guide-section-body">{t(section.bodyKey)}</p> : null}
      {section.tips?.length ? <GuideTipList tips={section.tips} t={t} /> : null}
      {section.subsections?.map((sub) => (
        <div
          key={sub.titleKey}
          className={`vc-guide-subsection vc-guide-subsection--${sub.variant ?? 'default'}`}
        >
          <h4 className="vc-guide-subsection-title">{t(sub.titleKey)}</h4>
          <GuideStepList keys={sub.bulletKeys} t={t} />
        </div>
      ))}
      {section.bulletKeys?.length ? <GuideStepList keys={section.bulletKeys} t={t} /> : null}
      {section.bodyKey && hasBodyOnly ? <p className="vc-guide-section-body vc-guide-section-body--solo">{t(section.bodyKey)}</p> : null}
    </section>
  )
}

function GuideParagraphBody({ storyId, t }: { storyId: GuideStoryId; t: (key: string) => string }) {
  const paragraphs = guideParagraphKeys(storyId)
  const [lead, ...rest] = paragraphs

  return (
    <div className="vc-guide-modal-body">
      {lead ? <p className="vc-guide-modal-lead">{t(lead)}</p> : null}
      {rest.length ? <GuideStepList keys={rest} t={t} /> : null}
    </div>
  )
}

export default function GuideArticleModal({ storyId, onClose }: Props) {
  const { t } = useTranslation()
  const layout = getGuideStoryLayout(storyId)
  const dateKey = `vc.story.${storyId}Date`
  const titleKey = `vc.story.${storyId}Title`

  return (
    <div className="vc-guide-modal-backdrop" role="presentation" onClick={onClose}>
      <article
        className={`vc-guide-modal${layout ? ' vc-guide-modal--structured' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="vc-guide-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        {!layout?.hideCover ? (
          <div className="vc-guide-modal-cover-wrap">
            <ModalCloseButton
              className="vc-guide-modal-close"
              variant="dark"
              onClick={onClose}
              aria-label={t('vc.story.close')}
            />
            <div className="vc-guide-modal-cover" data-story={storyId}>
              <img src={getStoryCoverUrl(storyId, 960)} alt="" loading="lazy" decoding="async" />
              <div className="vc-guide-modal-cover-shade" aria-hidden="true" />
            </div>
          </div>
        ) : (
          <div className="vc-guide-modal-hero vc-guide-modal-hero--philosophy">
            <span className="vc-guide-modal-hero-badge">Verified</span>
            <ModalCloseButton
              className="vc-guide-modal-hero-close"
              onClick={onClose}
              aria-label={t('vc.story.close')}
            />
          </div>
        )}

        <div className="vc-guide-modal-content">
          <header className="vc-guide-modal-header">
            <time className="vc-guide-modal-date">{t(dateKey)}</time>
            <h2 id="vc-guide-modal-title" className="vc-guide-modal-title">
              {t(titleKey)}
            </h2>
          </header>

          {layout ? (
            <GuideStorySections layout={layout} t={t} />
          ) : (
            <GuideParagraphBody storyId={storyId} t={t} />
          )}
        </div>
      </article>
    </div>
  )
}
